import { z } from 'zod';
/**
 * The daily check-in (ADR-024, M-105..M-112).
 *
 * "How are you today?", then any number of words describing how somebody is
 * in body and in mind. What is not here matters as much as what is: there is
 * no score, no average, no streak and no trend, because ADR-024 keeps W-100's
 * refusal of any figure derived from a mood.
 */
/**
 * Five steps, stored as the number and rendered from this list.
 *
 * An index into a list of words, not a quantity. Stored as a number so the
 * labels below can be reworded without rewriting anybody's history, and
 * nothing sums, averages or fits a line through it.
 *
 * "Rough" rather than "bad" at the bottom on purpose: the day somebody feels
 * worst is the day they least want to tap a button that calls them bad.
 */
export const CHECKIN_RATINGS = [
    { value: 1, label: 'Rough', emoji: '🫠' },
    { value: 2, label: 'Not great', emoji: '😕' },
    { value: 3, label: 'OK', emoji: '😐' },
    { value: 4, label: 'Good', emoji: '🙂' },
    { value: 5, label: 'Great', emoji: '😄' },
];
export const checkinRatingSchema = z.number().int().min(1).max(5);
export const checkinFacetSchema = z.enum(['body', 'mind']);
export const checkinToneSchema = z.enum(['helps', 'hinders']);
export const CHECKIN_FACET_LABELS = {
    body: 'Your body',
    mind: 'Your head',
};
export const checkinWordSchema = z.object({
    id: z.string().uuid(),
    word: z.string(),
    emoji: z.string().nullable(),
    facet: checkinFacetSchema,
    tone: checkinToneSchema,
});
/** The catalogue, split the way it is shown. */
export const checkinWordsSchema = z.object({
    body: z.array(checkinWordSchema),
    mind: z.array(checkinWordSchema),
});
export const checkinSchema = z.object({
    date: z.string().date(),
    rating: checkinRatingSchema,
    /** Ids from the catalogue. An empty list is a valid answer, not a missing one. */
    wordIds: z.array(z.string().uuid()),
    updatedAt: z.string(),
});
export const saveCheckinSchema = z.object({
    date: z.string().date(),
    rating: checkinRatingSchema,
    wordIds: z.array(z.string().uuid()).max(40).default([]),
});
/**
 * Settings, which belong to the person rather than the household.
 *
 * `enabled` defaults to false everywhere it is created. Nobody is asked how
 * they feel because a release landed (M-107).
 */
export const checkinSettingsSchema = z.object({
    enabled: z.boolean(),
    /** A local time, read against the household timezone. Null means no reminder. */
    remindAt: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'A time of day, as HH:MM')
        .nullable(),
});
//# sourceMappingURL=checkin.js.map